// Alte Kundenakten verbundener Kontakte (PO 28.09.2026).
//
// „Wenn Kontakte durch Unternehmensverbund oder Haushalt verbunden sind, dann
// auch bei denen anzeigen." Eine Akte gehört genau EINEM Kontakt — die
// Eheleute, die Firma und ihre Grundbesitz-GmbH sehen sie trotzdem, als
// eigenen Ordner an der Wurzel ihrer Akte. Der Ansprechpartner (Mitarbeiter)
// sah vom 04.10. bis 08.10.2026 auch die Akte seines Unternehmens — auf Wunsch
// wieder raus: nur im Firmenverbund. Gelesen wird dabei die Akte des
// Inhabers (mit dessen Kontakt-Id); nichts wird kopiert, und der Server prüft
// die Sicht auf den Inhaber wie bei jedem anderen Dokumentabruf.
//
// Der Teilbaum ist nur zum Lesen da: Verschieben, Umbenennen und neue Ordner
// gehören in die Akte des Inhabers.

/** Wurzel des Teilbaums; U+2060 ist unsichtbar und nicht tippbar (vgl. KMVS_WURZEL). */
export const VERBUND_WURZEL = "⁠AKTE/";

export type VerbundKontakt = {
  id: number;
  name: string;
  /** Woher die Verbindung kommt — steht im Ordnernamen. */
  art: "Haushalt" | "Unternehmensverbund";
};

export const verbundPfad = (id: number): string => `${VERBUND_WURZEL}${id}/`;

export const istVerbundPfad = (pfad: string): boolean => pfad.startsWith(VERBUND_WURZEL);

/**
 * Zerlegt einen Pfad im Teilbaum: wessen Akte, und welcher Ordner darin.
 * `"⁠AKTE/12/Verträge/"` → `{ id: 12, innen: "Verträge/", basis: "⁠AKTE/12/" }`.
 */
export const fremdeAkte = (pfad: string): { id: number; innen: string; basis: string } | null => {
  if (!istVerbundPfad(pfad)) return null;
  const m = /^(\d+)\/(.*)$/.exec(pfad.slice(VERBUND_WURZEL.length));
  if (!m) return null;
  const id = Number(m[1]);
  return { id, innen: m[2], basis: verbundPfad(id) };
};

/** Ordnername an der Wurzel: „Erika Mustermann (Haushalt)". */
export const verbundOrdnerName = (k: VerbundKontakt): string => `${k.name} (${k.art})`;

/**
 * Verbundene Kontakte aus den Umfeld-Abfragen, ohne den Kontakt selbst und
 * ohne Dubletten. Der Haushalt kommt zuerst — er ist bei Personen die nähere
 * Verbindung.
 */
export const verbundeneKontakte = (
  selbst: number,
  haushalt: { mitglieder?: Array<{ id: number; name: string | null }> } | null,
  verbund: { firmen?: Array<{ id: number; name: string | null }> } | null,
): VerbundKontakt[] => {
  const out: VerbundKontakt[] = [];
  const gesehen = new Set<number>([selbst]);
  const dazu = (liste: Array<{ id: number; name: string | null }> | undefined, art: VerbundKontakt["art"]) => {
    for (const k of liste ?? []) {
      if (!k?.id || gesehen.has(k.id)) continue;
      gesehen.add(k.id);
      out.push({ id: k.id, name: k.name?.trim() || `Kontakt ${k.id}`, art });
    }
  };
  dazu(haushalt?.mitglieder, "Haushalt");
  dazu(verbund?.firmen, "Unternehmensverbund");
  return out;
};
